import { useState, useRef, useEffect } from 'react';
import {
  Loader2,
  UserPlus,
  MoreVertical,
  Edit2,
  UserX,
  UserCheck,
  Shield,
  Search,
  Filter,
  Trash2,
  Eye,
  EyeOff,
  BookOpen,
  Check,
  X,
  Phone,
  MapPin,
  User as UserIcon,
  Users as UsersIcon,
} from 'lucide-react';
import { StatusChip, Button } from '@lms/ui';
import {
  useUsers,
  useCreateUser,
  useDeactivateUser,
  useActivateUser,
  useDeleteUser,
} from '../hooks/useUsers';
import { usePrograms } from '../hooks/usePrograms';
import { AssignRoleModal } from '../components/AssignRoleModal';
import { EditUserModal } from '../components/EditUserModal';
import { Pagination } from '../components/Pagination';
import type { User } from '@lms/shared/types';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import { UserDetailDrawer } from '../components/UserDetailDrawer';

export function Users() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 10;

  // Program autocomplete filter state
  const [selectedProgram, setSelectedProgram] = useState<{
    id: string;
    name: string;
    code: string;
  } | null>(null);
  const [programInput, setProgramInput] = useState('');
  const [isProgramDropdownOpen, setIsProgramDropdownOpen] = useState(false);
  const programDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch suggested programs matching input (or active programs if empty)
  const { data: programsData, isLoading: isProgramsLoading } = usePrograms({
    search: programInput.trim() || undefined,
    limit: 8,
  });
  const suggestedPrograms = programsData?.items || [];

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (programDropdownRef.current && !programDropdownRef.current.contains(e.target as Node)) {
        setIsProgramDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const { t } = useLanguage();
  const { data, isLoading } = useUsers({
    page,
    limit,
    search,
    programId: selectedProgram?.id,
  });
  const users = data?.items || [];

  const { mutate: deactivate } = useDeactivateUser();
  const { mutate: activate } = useActivateUser();
  const { mutate: deleteUser } = useDeleteUser();
  const [activeActionsId, setActiveActionsId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <section className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 pb-6 border-b border-outline-variant/10">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-headline font-extrabold text-on-surface tracking-tight m-0">
              <Bilingual {...copy.users.listTitle} />
            </h1>
            {data?.meta?.total !== undefined && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold shadow-sm">
                <UsersIcon size={13} />
                {data.meta.total} Pengguna
              </span>
            )}
          </div>
          <p className="text-sm font-body text-on-surface-variant max-w-2xl leading-relaxed m-0">
            <Bilingual {...copy.users.listSubtitle} />
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setIsAddModalOpen(true)}
          leftIcon={<UserPlus size={14} />}
          className="text-xs font-bold px-5 shadow-level1"
        >
          <Bilingual {...copy.users.btnAddUser} />
        </Button>
      </section>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between bg-surface-container-low/40 p-3 rounded-2xl">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-1">
          {/* Search Input */}
          <div className="relative w-full sm:w-80 group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-40 group-focus-within:text-primary group-focus-within:opacity-100 transition-all"
              size={18}
            />
            <input
              type="text"
              placeholder={t(copy.users.searchPlaceholder)}
              className="w-full bg-surface-container-lowest border-none rounded-xl pl-12 pr-4 py-2.5 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all shadow-sm"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
            />
          </div>

          {/* Program Suggestion Combobox */}
          <div className="relative w-full sm:w-80" ref={programDropdownRef}>
            <div className="relative group">
              <BookOpen
                className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-40 group-focus-within:text-primary group-focus-within:opacity-100 transition-all"
                size={18}
              />
              <input
                type="text"
                placeholder={
                  selectedProgram ? selectedProgram.name : t(copy.users.filterProgramPlaceholder)
                }
                value={programInput}
                onFocus={() => setIsProgramDropdownOpen(true)}
                onChange={e => {
                  setProgramInput(e.target.value);
                  setIsProgramDropdownOpen(true);
                }}
                className={`w-full bg-surface-container-lowest border-none rounded-xl pl-12 pr-10 py-2.5 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all shadow-sm ${
                  selectedProgram ? 'text-primary font-bold placeholder:text-primary' : ''
                }`}
              />
              {selectedProgram ? (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProgram(null);
                    setProgramInput('');
                    setPage(1);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-on-surface-variant hover:text-error transition-colors rounded-full"
                  title={t(copy.users.clearProgramFilter)}
                >
                  <X size={16} />
                </button>
              ) : isProgramsLoading ? (
                <Loader2
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant/40 animate-spin"
                  size={16}
                />
              ) : null}
            </div>

            {/* Autocomplete Suggestions Dropdown */}
            {isProgramDropdownOpen && (
              <div className="absolute left-0 right-0 top-full mt-2 z-50 bg-surface-container-lowest rounded-2xl p-2 shadow-level4 border border-outline-variant/10 max-h-64 overflow-y-auto animate-fadeIn">
                {suggestedPrograms.length > 0 ? (
                  suggestedPrograms.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setSelectedProgram({ id: p.id, name: p.name, code: p.code });
                        setProgramInput('');
                        setIsProgramDropdownOpen(false);
                        setPage(1);
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-xl text-left text-xs font-body transition-colors cursor-pointer ${
                        selectedProgram?.id === p.id
                          ? 'bg-primary/10 text-primary font-bold'
                          : 'hover:bg-surface-container-high text-on-surface'
                      }`}
                    >
                      <div className="flex flex-col min-w-0 pr-2">
                        <span className="truncate font-semibold">{p.name}</span>
                        <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant opacity-70">
                          {p.code}
                        </span>
                      </div>
                      {selectedProgram?.id === p.id && (
                        <Check size={16} className="text-primary shrink-0" />
                      )}
                    </button>
                  ))
                ) : (
                  <div className="p-4 text-center text-xs text-on-surface-variant font-body">
                    {t(copy.users.noProgramsFound)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Filter size={14} />}
            className="text-xs font-bold tracking-wide"
          >
            <Bilingual {...copy.users.btnFilterRoles} />
          </Button>
        </div>
      </div>

      {/* Active Program Filter Chip */}
      {selectedProgram && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-label font-bold text-on-surface-variant uppercase tracking-wider">
            <Bilingual {...copy.users.filterProgramLabel} />
          </span>
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-xs font-bold shadow-sm">
            <BookOpen size={14} />
            <span>
              {selectedProgram.name} ({selectedProgram.code})
            </span>
            <button
              type="button"
              onClick={() => {
                setSelectedProgram(null);
                setProgramInput('');
                setPage(1);
              }}
              className="hover:text-error transition-colors p-0.5 rounded-full"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Directory Table Matrix */}
      <div className="overflow-hidden rounded-3xl bg-surface-container-low shadow-level2">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[380px] space-y-4">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-sm font-body text-on-surface-variant font-medium">
              <Bilingual {...copy.users.syncingDirectory} />
            </p>
          </div>
        ) : (
          <>
            {/* Table Matrix Header & Rows */}
            <div className="overflow-x-auto w-full">
              <div className="min-w-0 md:min-w-[860px]">
                {/* Header */}
                <div className="hidden grid-cols-[1.8fr_1.3fr_1fr_1fr_90px] gap-6 bg-surface-container-high px-8 py-4 text-[11px] font-label font-bold uppercase tracking-wider text-on-surface-variant md:grid">
                  <div>
                    <Bilingual {...copy.users.tableHeaderUser} />
                  </div>
                  <div>
                    <Bilingual {...copy.users.tableHeaderContactDomicile} />
                  </div>
                  <div>
                    <Bilingual {...copy.users.tableHeaderRoles} />
                  </div>
                  <div>
                    <Bilingual {...copy.users.tableHeaderStatus} />
                  </div>
                  <div className="text-right">
                    <Bilingual {...copy.users.actionLabel} />
                  </div>
                </div>

                {/* Directory Rows */}
                <div className="space-y-2 bg-surface-container-low p-2">
                  {users.map(user => {
                    const roleObj =
                      user.roles &&
                      user.roles.length > 0 &&
                      typeof user.roles[0] === 'object' &&
                      'role' in user.roles[0]
                        ? (user.roles[0] as { role: { name: string } }).role
                        : null;
                    const roleName =
                      roleObj?.name ||
                      (typeof user.roles?.[0] === 'string' ? user.roles[0] : 'USER');

                    const userMeta =
                      ((user as unknown as { legacyMetadata?: Record<string, unknown> })
                        ?.legacyMetadata as Record<string, unknown>) || {};
                    const userDomisili =
                      (user as unknown as { domisili?: string })?.domisili ||
                      (userMeta.domisili as string) ||
                      (userMeta.city as string) ||
                      '';

                    return (
                      <div
                        key={user.id}
                        onClick={e => {
                          if ((e.target as HTMLElement).closest('button')) return;
                          setDetailUserId(user.id);
                        }}
                        className="group relative flex cursor-pointer flex-col items-start gap-4 rounded-2xl bg-surface-container-lowest p-5 transition-all duration-150 hover:bg-surface md:grid md:grid-cols-[1.8fr_1.3fr_1fr_1fr_90px] md:items-center md:gap-6 md:px-8 md:py-4"
                      >
                        {/* 1. Pengguna (Avatar + Nama + Gender badge + Email) */}
                        <div className="w-full flex items-center gap-3.5">
                          <div
                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl font-headline text-base font-bold transition-transform group-hover:scale-105 ${
                              user.gender === 'FEMALE'
                                ? 'bg-tertiary/15 text-tertiary'
                                : user.gender === 'MALE'
                                  ? 'bg-primary/10 text-primary'
                                  : 'bg-surface-container-highest text-on-surface-variant'
                            }`}
                          >
                            {user.name ? user.name.charAt(0).toUpperCase() : <UserIcon size={20} />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-headline font-bold text-on-surface group-hover:text-primary transition-colors truncate">
                                {user.name}
                              </span>
                              {user.gender && (
                                <span
                                  className={`text-[9px] font-label font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                    user.gender === 'FEMALE'
                                      ? 'bg-tertiary/15 text-tertiary'
                                      : 'bg-primary/10 text-primary'
                                  }`}
                                >
                                  {user.gender === 'FEMALE' ? 'Akhwat' : 'Ikhwan'}
                                </span>
                              )}
                            </div>
                            <div className="text-xs font-mono text-on-surface-variant font-medium mt-0.5 truncate">
                              {user.email}
                            </div>
                          </div>
                        </div>

                        {/* 2. Kontak & Domisili */}
                        <div className="flex flex-col gap-1 w-full md:w-auto text-xs font-body">
                          <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                            <Bilingual {...copy.users.tableHeaderContactDomicile} />
                          </span>
                          <div className="flex items-center gap-1.5 text-on-surface font-medium">
                            <Phone size={13} className="text-primary shrink-0" />
                            <span className="truncate">{user.phone || '-'}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-on-surface-variant text-[11px]">
                            <MapPin size={13} className="text-on-surface-variant/70 shrink-0" />
                            <span className="truncate">
                              {userDomisili || 'Domisili belum diisi'}
                            </span>
                          </div>
                        </div>

                        {/* 3. Peran (Roles) */}
                        <div className="flex items-center justify-between w-full md:w-auto">
                          <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                            <Bilingual {...copy.users.tableHeaderRoles} />
                          </span>
                          <span
                            className={`text-[10px] font-label font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg ${
                              roleName === 'ADMIN'
                                ? 'bg-tertiary/15 text-tertiary'
                                : roleName === 'TEACHER'
                                  ? 'bg-secondary/25 text-on-surface'
                                  : roleName === 'PARTICIPANT'
                                    ? 'bg-success-container text-primary'
                                    : 'bg-surface-container-high text-on-surface'
                            }`}
                          >
                            {roleName}
                          </span>
                        </div>

                        {/* 4. Status Akun */}
                        <div className="flex items-center justify-between w-full md:w-auto">
                          <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                            <Bilingual {...copy.users.tableHeaderStatus} />
                          </span>
                          <div>
                            <StatusChip
                              label={t(
                                user.isActive !== false
                                  ? copy.users.statusActive
                                  : copy.users.statusInactive
                              )}
                              variant={user.isActive !== false ? 'success' : 'error'}
                            />
                          </div>
                        </div>

                        {/* 5. Aksi (Quick View Eye + More Dropdown) */}
                        <div className="flex items-center justify-end gap-1.5 w-full md:w-auto relative">
                          <button
                            type="button"
                            onClick={() => setDetailUserId(user.id)}
                            className="rounded-xl p-2 text-on-surface-variant transition-all hover:bg-primary/10 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
                            title={t(copy.users.quickViewDetail)}
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setActiveActionsId(activeActionsId === user.id ? null : user.id)
                            }
                            className="rounded-xl p-2 text-on-surface-variant transition-all hover:bg-primary/10 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/20 active:scale-95"
                          >
                            <MoreVertical size={16} />
                          </button>

                          {/* Dropdown Actions */}
                          {activeActionsId === user.id && (
                            <div className="absolute right-0 top-11 z-50 mt-1 w-52 rounded-2xl bg-surface-container-lowest py-2 font-body shadow-level4 animate-fadeIn">
                              {user.isActive !== false ? (
                                <button
                                  onClick={() => {
                                    deactivate(user.id);
                                    setActiveActionsId(null);
                                  }}
                                  className="w-full text-left px-4 py-2.5 hover:bg-error/5 text-error flex items-center gap-3 text-xs font-bold uppercase tracking-wider"
                                >
                                  <UserX size={14} /> <Bilingual {...copy.users.btnDeactivate} />
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    activate(user.id);
                                    setActiveActionsId(null);
                                  }}
                                  className="w-full text-left px-4 py-2.5 hover:bg-success/5 text-success flex items-center gap-3 text-xs font-bold uppercase tracking-wider"
                                >
                                  <UserCheck size={14} /> <Bilingual {...copy.users.btnActivate} />
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setEditUserId(user.id);
                                  setActiveActionsId(null);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-surface-container-low text-on-surface flex items-center gap-3 text-xs font-bold uppercase tracking-wider"
                              >
                                <Edit2 size={14} /> <Bilingual {...copy.users.btnEditIdentity} />
                              </button>
                              <button
                                onClick={() => {
                                  setDetailUserId(user.id);
                                  setActiveActionsId(null);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-surface-container-low text-on-surface flex items-center gap-3 text-xs font-bold uppercase tracking-wider"
                              >
                                <Eye size={14} /> <Bilingual {...copy.users.detailTitle} />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedUser(user);
                                  setIsRoleModalOpen(true);
                                  setActiveActionsId(null);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-primary/5 text-primary flex items-center gap-3 text-xs font-bold uppercase tracking-wider"
                              >
                                <Shield size={14} /> <Bilingual {...copy.users.btnAssignRole} />
                              </button>

                              <div className="my-1 h-2" />

                              <button
                                onClick={() => {
                                  if (window.confirm(t(copy.users.confirmDelete))) {
                                    deleteUser(user.id);
                                  }
                                  setActiveActionsId(null);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-error/10 text-error flex items-center gap-3 text-xs font-bold uppercase tracking-wider"
                              >
                                <Trash2 size={14} /> <Bilingual {...copy.users.btnDelete} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {users.length === 0 && (
                  <div className="px-8 py-20 text-center bg-surface-container-low/20 italic text-on-surface-variant text-sm">
                    <Bilingual {...copy.users.emptyState} />
                  </div>
                )}

                {data?.meta && (
                  <Pagination
                    currentPage={data.meta.page}
                    totalPages={data.meta.totalPages}
                    onPageChange={setPage}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add User Modal */}
      {isAddModalOpen && <AddUserModal onClose={() => setIsAddModalOpen(false)} />}

      {/* Role Assignment Modal */}
      {isRoleModalOpen && selectedUser && (
        <AssignRoleModal
          onClose={() => {
            setIsRoleModalOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
        />
      )}

      {/* Edit User Modal */}
      {editUserId && (
        <EditUserModal
          userId={editUserId}
          onClose={() => setEditUserId(null)}
          onSaved={() => setEditUserId(null)}
        />
      )}

      {/* User Details Drawer */}
      {detailUserId && (
        <UserDetailDrawer
          userId={detailUserId}
          programId={selectedProgram?.id}
          onClose={() => setDetailUserId(null)}
        />
      )}
    </div>
  );
}

function AddUserModal({ onClose }: { onClose: () => void }) {
  const { mutate: createUser, isPending } = useCreateUser();
  const { t } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'PARTICIPANT',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createUser(formData, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  return (
    <div className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm z-50 flex justify-center items-center p-4 animate-fadeIn">
      <div className="bg-surface-container-lowest rounded-3xl w-full max-w-md shadow-level4 overflow-hidden animate-slideUp border border-outline-variant/10">
        <div className="p-6 border-b border-outline-variant/10 bg-surface-container-low flex justify-between items-center">
          <div>
            <h2 className="text-xl font-headline font-bold text-on-surface m-0 tracking-tight">
              <Bilingual {...copy.users.modalTitle} />
            </h2>
            <p className="text-xs font-body text-on-surface-variant m-0 mt-0.5">
              <Bilingual {...copy.users.modalSubtitle} />
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-on-surface-variant hover:text-primary rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-label font-bold text-on-surface">
              <Bilingual {...copy.users.labelName} />
            </label>
            <input
              required
              className="w-full bg-surface-container-low border-none rounded-xl px-4 py-2.5 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder={t(copy.users.placeholderName)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-label font-bold text-on-surface">
              <Bilingual {...copy.users.labelEmail} />
            </label>
            <input
              required
              type="email"
              className="w-full bg-surface-container-low border-none rounded-xl px-4 py-2.5 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all"
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              placeholder={t(copy.users.placeholderEmail)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-label font-bold text-on-surface">
              <Bilingual {...copy.users.labelPassword} />
            </label>
            <div className="relative">
              <input
                required
                type={showPassword ? 'text' : 'password'}
                className="w-full bg-surface-container-low border-none rounded-xl pl-4 pr-11 py-2.5 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant/60 hover:text-on-surface transition-colors p-1"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-label font-bold text-on-surface">
              <Bilingual {...copy.users.labelRole} />
            </label>
            <select
              className="w-full bg-surface-container-low border-none rounded-xl px-4 py-2.5 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all appearance-none cursor-pointer"
              value={formData.role}
              onChange={e => setFormData({ ...formData, role: e.target.value })}
            >
              <option value="PARTICIPANT">{t(copy.users.roleParticipant)}</option>
              <option value="ADMIN">{t(copy.users.roleAdmin)}</option>
              <option value="TEACHER">{t(copy.users.roleTeacher)}</option>
            </select>
          </div>

          <div className="pt-4 flex justify-end gap-3 border-t border-outline-variant/10">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-xs font-semibold"
            >
              <Bilingual {...copy.users.btnCancel} />
            </Button>
            <Button
              type="submit"
              size="sm"
              className="px-6 text-xs font-bold shadow-level1"
              disabled={isPending}
            >
              {isPending ? (
                <Loader2 className="animate-spin" size={14} />
              ) : (
                <Bilingual {...copy.users.btnAuthorize} />
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
